'use client';

import { cn, condition, isWindow, numberToPx, prolongEvent } from '@/helpers/common';
import useBreakpoints from '@/hooks/useBreakpoints';
import { useToggle } from '@/hooks/useToggle';
import { TGenericObject } from '@/ts/types';
import {
  MutableRefObject,
  ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface ITooltipComponentProps {
  enableTooltipOnMobile?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  children: ReactNode;
  shiftGap?: number;
  edgeGap?: number;
  tooltip?: ReactNode;
  className?: string;
}

const Tooltip = ({
  children,
  placement: _placement = 'top',
  className,
  enableTooltipOnMobile,
  tooltip,
  edgeGap = 15,
  shiftGap = 10,
}: ITooltipComponentProps) => {
  const { getIsMin, getIsMax } = useBreakpoints({ min: [768], max: [767] });
  const currentTargetRef = useRef<HTMLElement & TGenericObject>(null);
  const [placement, setPlacement] = useState(_placement);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [{ top, left, translateX, translateY }, setAxis] = useState({
    top: 0,
    left: 0,
    translateX: '0',
    translateY: '0',
  });
  const {
    toggled: hovered,
    toggleOn: hoverOn,
    toggleOff: hoverOff,
    toggle: toggleHover,
  } = useToggle();

  const handleAxis = useCallback(
    (currentTarget: HTMLElement) => {
      const { width: targetWidth, top, height, left } = currentTarget.getBoundingClientRect();

      if (tooltipRef.current) {
        if (placement === 'top') {
          setAxis({
            top: top - shiftGap,
            left: left + targetWidth / 2,
            translateX: '-50%',
            translateY: '-100%',
          });
        }

        if (placement === 'bottom') {
          setAxis({
            top: top + shiftGap + height,
            left: left + targetWidth / 2,
            translateX: '-50%',
            translateY: '0',
          });
        }

        if (placement === 'left') {
          const { width: tooltipWidth } = tooltipRef.current.getBoundingClientRect();
          const { innerWidth: windowWidth } = window;

          const isNotEnoughSpaceOnLeft = left - shiftGap - tooltipWidth - edgeGap < 0;
          if (
            isNotEnoughSpaceOnLeft &&
            left + targetWidth + shiftGap + edgeGap + tooltipWidth < windowWidth
          ) {
            return setPlacement('right');
          }
          if (
            isNotEnoughSpaceOnLeft &&
            left + targetWidth + shiftGap + edgeGap + tooltipWidth > windowWidth
          ) {
            return setPlacement('top');
          }

          setAxis({
            top: top + height / 2,
            left: left - shiftGap,
            translateX: '-100%',
            translateY: '-50%',
          });
        }

        if (placement === 'right') {
          const { width: tooltipWidth } = tooltipRef.current.getBoundingClientRect();
          const { innerWidth: windowWidth } = window;

          const isNotEnoughSpaceOnRight =
            left + targetWidth + shiftGap + edgeGap + tooltipWidth > windowWidth;

          if (isNotEnoughSpaceOnRight && left - shiftGap - tooltipWidth - edgeGap > 0) {
            return setPlacement('left');
          }
          if (isNotEnoughSpaceOnRight && left - shiftGap - tooltipWidth - edgeGap < 0) {
            return setPlacement('top');
          }

          setAxis({
            top: top + height / 2,
            left: left + targetWidth + shiftGap,
            translateX: '0',
            translateY: '-50%',
          });
        }
      }

      return null;
    },
    [placement, edgeGap, shiftGap],
  );

  const handleMouseMove = useCallback(
    ({ currentTarget, target }: HTMLElement & TGenericObject) => {
      if (!currentTargetRef.current) {
        (currentTarget as MutableRefObject<HTMLElement & TGenericObject>).current = currentTarget;
      }

      const { top: targetTop } = target.getBoundingClientRect();

      const { top: currentTargetTop, bottom: currentTargetBottom } =
        currentTarget.getBoundingClientRect();
      if (getIsMin(768)) {
        if (targetTop < currentTargetTop || targetTop > currentTargetBottom) {
          return hoverOff();
        }
        if (!hovered) {
          hoverOn();
        }
        return handleAxis(currentTarget);
      }

      return undefined;
    },
    [hovered, handleAxis, getIsMin, hoverOff, hoverOn],
  );
  const handleTouchStart = useCallback(
    ({ currentTarget, target }: HTMLElement & TGenericObject) => {
      if (!currentTargetRef.current) {
        (currentTarget as MutableRefObject<HTMLElement & TGenericObject>).current = currentTarget;
      }

      const { top: targetTop } = target.getBoundingClientRect();

      const { top: currentTargetTop, bottom: currentTargetBottom } =
        currentTarget.getBoundingClientRect();

      if (getIsMax(767) && enableTooltipOnMobile) {
        if (targetTop < currentTargetTop || targetTop > currentTargetBottom) {
          return hoverOff();
        }
        toggleHover();

        return handleAxis(currentTarget);
      }

      return undefined;
    },
    [handleAxis, enableTooltipOnMobile, getIsMax, hoverOff, toggleHover],
  );

  useEffect(() => {
    const callback = () => {
      handleAxis(currentTargetRef.current as HTMLElement);
    };

    if (hovered && currentTargetRef.current && isWindow()) {
      window.addEventListener('scroll', callback);
    }
    return () => {
      window.removeEventListener('scroll', callback);
    };
  }, [hovered, handleAxis]);

  const child = useMemo(
    () =>
      (Array.isArray(children)
        ? children.find((child) => isValidElement(child))
        : children) as ReactNode,
    [children],
  );

  if (isValidElement(child)) {
    return (
      <>
        <span
          ref={tooltipRef}
          style={condition(
            Boolean(hovered && tooltip),
            {
              top: numberToPx(top) ?? '0',
              left: numberToPx(left) ?? '0',
              transform: `translateX(${translateX ?? 0}) translateY(${translateY ?? 0})`,
            },
            {},
          )}
          className={cn(
            'fixed opacity-0 transition-opacity duration-300 ease-out',
            {
              'w-max p-2 rounded-(--main-border-radius) bg-[#1d1d1d] text-xs leading-[14.4px] text-center text-[#f4f4f4] z-50 opacity-80':
                Boolean(hovered && tooltip),
            },
            className,
          )}
        >
          {Boolean(hovered && tooltip) && <>{tooltip}</>}
        </span>

        {cloneElement<TGenericObject>(child as any, {
          onTouchStart: prolongEvent(() => {
            const { props } = child as any;
            if (props && props?.onTouchStart) {
              props?.onTouchStart();
            }
          }, handleTouchStart),
          onMouseMove: prolongEvent(() => {
            const { props } = child as any;
            if (props && props?.onMouseMove) {
              props?.onMouseMove();
            }
          }, handleMouseMove),
          onMouseLeave: prolongEvent(
            () => {
              const { props } = child as any;
              if (props && props?.onMouseLeave) {
                props?.onMouseLeave();
              }
            },
            hoverOff,
            () => setPlacement(_placement),
          ),
        })}
      </>
    );
  }

  console.error('Tooltip: children must be a valid React single element');
  return null;
};

export default Tooltip;
